/**
 * Trunnion AI - Audit Export Button
 * Export with format selector.
 */

import { useState } from 'react';
import { Download, FileJson, FileText, Shield } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { useAuditExport } from '@/hooks/queries/useAuditLogs';
import type { ATOExportConfig } from '@/types';

export function AuditExportButton() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [format, setFormat] = useState<'json' | 'csv' | 'stig_viewer'>('json');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includePayloads, setIncludePayloads] = useState(true);

  const exportMutation = useAuditExport();

  const handleExport = () => {
    const config: ATOExportConfig = {
      format,
      dateRange: {
        start: startDate || new Date(Date.now() - 30 * 86400000).toISOString(),
        end: endDate || new Date().toISOString(),
      },
      includePayloads,
      includeCorrelations: true,
    };
    exportMutation.mutate(config, {
      onSuccess: () => setDialogOpen(false),
    });
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        icon={<Download className="h-4 w-4" />}
        onClick={() => setDialogOpen(true)}
      >
        Export
      </Button>

      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Export Audit Log"
        description="Export audit events for compliance and ATO documentation."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              loading={exportMutation.isPending}
              icon={<Download className="h-4 w-4" />}
            >
              Export
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Format"
            value={format}
            onChange={(e) => setFormat(e.target.value as typeof format)}
            options={[
              { value: 'json', label: 'JSON' },
              { value: 'csv', label: 'CSV' },
              { value: 'stig_viewer', label: 'STIG Viewer Compatible' },
            ]}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include-payloads"
              checked={includePayloads}
              onChange={(e) => setIncludePayloads(e.target.checked)}
              className="rounded border-trunnion-slate-600"
            />
            <label
              htmlFor="include-payloads"
              className="text-sm text-trunnion-slate-200"
            >
              Include event payloads
            </label>
          </div>

          <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-3">
            <div className="flex items-center gap-2 text-xs text-trunnion-slate-400">
              {format === 'json' && <FileJson className="h-4 w-4" />}
              {format === 'csv' && <FileText className="h-4 w-4" />}
              {format === 'stig_viewer' && <Shield className="h-4 w-4" />}
              <span>
                {format === 'json' && 'JSON format with full event details'}
                {format === 'csv' && 'CSV format for spreadsheet analysis'}
                {format === 'stig_viewer' && 'STIG Viewer compatible format for ATO compliance'}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
